import { StatusBadge } from '../ui/StatusBadge'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { Clock, User, FileText, AlertCircle, RefreshCw, Bookmark } from 'lucide-react'

interface TaskCardProps {
  task: {
    id: string
    persona_id: string
    status: string
    content_type: string
    channel: string
    topic: string
    brief: string | null
    created_at: string
    completed_at: string | null
    error_message: string | null
  }
  personaName?: string
  sourcesCount?: number
  onClick?: () => void
  onCancel?: () => void
  onRerun?: () => void
}

export function TaskCard({ task, personaName, sourcesCount = 0, onClick, onCancel, onRerun }: TaskCardProps) {
  const canCancel = task.status === 'pending' || task.status === 'running'
  const canRerun = task.status === 'failed' || task.status === 'cancelled'
  const hasError = task.status === 'failed' && task.error_message

  return (
    <div 
      className="card hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-slate-800">{task.topic}</h3>
            <StatusBadge status={task.status} />
          </div>
          <p className="text-sm text-slate-600 line-clamp-2">
            {task.brief || 'Sin descripción'}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {hasError && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 line-clamp-2">{task.error_message}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
        <div className="flex items-center gap-1">
          <FileText className="w-4 h-4" />
          <span>{task.content_type}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs">
            {task.channel}
          </span>
        </div>
        {sourcesCount > 0 && (
          <span className="text-xs text-slate-500 flex items-center gap-1" title={`${sourcesCount} fuente${sourcesCount !== 1 ? 's' : ''}`}>
            <Bookmark className="w-3.5 h-3.5" />
            {sourcesCount} fuentes
          </span>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="flex items-center gap-4 text-xs text-slate-500">
          {personaName && (
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              <span>{personaName}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>
              {formatDistanceToNow(new Date(task.created_at), { 
                addSuffix: true, 
                locale: es 
              })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canRerun && onRerun && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onRerun()
              }}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Reintentar
            </button>
          )}
          {canCancel && onCancel && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onCancel()
              }}
              className="text-xs text-red-600 hover:text-red-700 font-medium"
            >
              Cancelar
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
